import React, { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { CalendarClock, Wallet } from "lucide-react";
import Spinner1 from "../common/spinners";
import moment from "moment";
import { useBills } from "../../context/paymentAndBillsContext";
import { formatCurrency } from "../../utils/utilities";

const PaymentAndBills = () => {
  const { userBills, loading, findPatientBillsAlt } = useBills();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    if (!userBills?.length) {
      findPatientBillsAlt();
    }
  }, []);

  const filteredData = useMemo(
    () =>
      (Array.isArray(userBills) ? userBills : []).filter(
        (item) =>
          item?.bill_source?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.transaction_type?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.patientRequestItem?.drug?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.patientRequestItem?.labTest?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.service?.item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [userBills, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <>
      <div id="bills" className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Payment & billing</h5>
          <p>Track billed items, payment methods, transaction types, and status without leaving the overview page.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search billed item, type, or source"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No billing records found</h6>
            <p>Charges and payments tied to your account will show up here when available.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Amount</th>
                  <th>Date & Time</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => {
                  const itemName =
                    item?.patientRequestItem?.drug?.name ||
                    item?.patientRequestItem?.labTest?.name ||
                    (item?.admission ? "Admission" : null) ||
                    item?.service?.item?.name ||
                    "---";

                  return (
                    <tr key={item?.id || index}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <span className="data-icon-avatar">
                            <Wallet size={18} />
                          </span>
                          <div>
                            <div className="data-cell-title">{itemName}</div>
                            <div className="data-cell-meta text-capitalize">
                              {item?.bill_source ?? "---"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="data-cell-title">{formatCurrency(item?.amount)}</div>
                        <div className="data-cell-meta">{item?.payment_method ?? "---"}</div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <span className="data-icon-avatar">
                            <CalendarClock size={18} />
                          </span>
                          <div>
                            <div className="data-cell-title">
                              {new Date(item.createdAt).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })}
                            </div>
                            <div className="data-cell-meta">
                              {moment(item.createdAt).format("dddd, Do MMMM, YYYY")}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="data-cell-title text-capitalize">{item?.transaction_type}</div>
                      </td>
                      <td>
                        <span className={`data-status-badge ${item?.status === 1 ? "is-success" : "is-danger"}`}>
                          {item?.status === 1 ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentAndBills;
